import { useEffect, useState } from 'react';

import { GroupsList } from './GroupsList';
import { Pie } from './Pie';
import {
  computePercentage,
  envVar,
  getSymbol,
} from '../../../helpers/functions';

import { Center, PieData, GridWidget } from '../types';
import { useUserStrategies } from '../../../context/UserStrategies';
import { CHART_COLORS_HEX, COLORS_HEX } from '../../../constants/colors';
import { Bar } from './Bar';
import { useParams } from 'react-router-dom';
import { InfoDataBond } from '../../Market/types';
import moment from 'moment';
import { CurrencyCodeEnum } from '../../../constants/enums';

export type BarData = {
  capital?: number;
  count?: number;
  coupon?: number;
  flow?: number;
  id: string;
  percentage?: number;
  year?: number;
  value?: number;
  color?: string;
};

export type Bond = {
  bondType: string;
  capital: number;
  coupon: string | number;
  date: string;
  currency: string;
  issuer: string;
  maturityDate: number;
};

type OverviewData = {
  [key: string]: {
    count: number;
    capital: number;
    coupon: number;
    flow: number;
    percentage?: number;
  };
};

export const COLUMNS_ALLOCATION = [
  'BREAKDOWN',
  'COUNT',
  'COUPON',
  'CAPITAL',
  'FLOW',
  'ISSUER',
  'MATURITY',
  'VALUE',
  'CURRENCY',
  'BOND TYPE',
] as const;

export const barColors = [
  CHART_COLORS_HEX.DARK_PURPLE,
  CHART_COLORS_HEX.PURPLE,
];

export const MyBondPortfolio = ({ height, width }: GridWidget) => {
  const { strategies } = useUserStrategies();
  const { id: strategyId } = useParams();

  const strategy = strategies.find((s) => s.id === strategyId)!;
  const items = strategy.strategyAssets.items;
  const currency = strategy?.currency || CurrencyCodeEnum.USD;

  const symbol = getSymbol(currency);

  const bonds = items.reduce(
    (arr, { asset: { info, type }, entryPrice, unit }) => {
      const {
        classificationData: { bondType = '' } = {},
        coupon = '',
        currency = '',
        issueData: { issuer = '' } = {},
        maturityDate = '',
      } = JSON.parse(info?.data ?? '{}') as InfoDataBond;

      const bondInvested = unit * entryPrice;

      return type === 'BOND'
        ? [
            ...arr,
            {
              bondType,
              capital: bondInvested,
              coupon: (bondInvested / 100) * +coupon,
              date: maturityDate,
              currency,
              issuer,
              maturityDate: moment(maturityDate).year(),
            },
          ]
        : arr;
    },
    [] as Bond[]
  );

  const maxYear = Math.max(...bonds.map(({ maturityDate }) => maturityDate));

  const overviewData: OverviewData = {};

  for (let i = moment().year(); i <= maxYear; ++i) {
    overviewData[`${i}`] = {
      count: 0,
      capital: 0,
      coupon: 0,
      flow: 0,
    };
  }

  Object.entries(overviewData).forEach(
    ([key, { count, capital, coupon, flow }]) => {
      const totalInvested = bonds.reduce(
        (total, { capital, maturityDate }) =>
          total + (+key <= maturityDate ? capital : 0),
        0
      );

      bonds.forEach((bond) => {
        if (+key <= bond.maturityDate) {
          ++count;
          coupon += +bond.coupon;
          +key === bond.maturityDate && (capital += bond.capital);
        }
      });

      overviewData[key] = {
        count,
        capital,
        coupon,
        flow: coupon + capital,
        percentage: computePercentage(totalInvested, coupon),
      };
    }
  );

  const [viewType, setViewType] = useState<(number | 'CAPITAL' | 'COUPON')[]>(
    []
  );

  const [columns, setColumns] = useState([...COLUMNS_ALLOCATION].slice(0, 5));

  const dataGraphic: BarData[] =
    viewType.length === 0
      ? Object.entries(overviewData)
          .sort((a, b) => +a[0] - +b[0])
          .map(([year, { capital, count, coupon, flow, percentage }]) => ({
            count,
            capital,
            coupon,
            flow,
            id: year,
            percentage,
            year: +year,
          }))
      : viewType.length === 1
      ? [
          {
            id: 'CAPITAL',
            value: Object.entries(overviewData).find(
              ([key]) => +key === viewType[0]
            )![1].capital,
            color: barColors[0],
          },
          {
            id: 'COUPON',
            value: Object.entries(overviewData).find(
              ([key]) => +key === viewType[0]
            )![1].coupon,
            color: barColors[1],
          },
        ]
      : bonds
          .filter(({ maturityDate }) => viewType[0] <= maturityDate)
          .map(({ issuer, capital, coupon }, i) => ({
            id: issuer + '@' + i,
            value: viewType[1] === 'CAPITAL' ? capital : +coupon,
            color: Object.values(COLORS_HEX)[i],
          }));

  const tableData = (
    viewType.length < 2
      ? [...dataGraphic]
      : bonds
          .filter(({ maturityDate }) => viewType[0] <= maturityDate)
          .map((e, i) => ({
            ...e,
            id: e.issuer + '@' + i,
            color: Object.values(COLORS_HEX)[i],
          }))
  ) as (Bond & BarData)[];

  const [totalValue, setTotalValue] = useState(0);

  const [center, setCenter] = useState<Center>({
    id: 'Total',
    value: 0,
  });

  useEffect(() => {
    setColumns(
      viewType.length === 0
        ? [...COLUMNS_ALLOCATION].slice(0, 5)
        : viewType.length === 1
        ? [COLUMNS_ALLOCATION[0]]
        : COLUMNS_ALLOCATION.slice(5)
    );

    setTotalValue(
      viewType.length === 0
        ? 0
        : viewType.length === 1
        ? dataGraphic.reduce((t, { value }) => t + value!, 0)
        : Object.entries(overviewData).reduce(
            (t, [key, obj]) =>
              +key === viewType[0]
                ? t + obj[String(viewType[1]).toLocaleLowerCase()]
                : t,
            0
          )
    );
  }, [viewType.length]);

  useEffect(() => {
    resetCenter();
  }, [totalValue]);

  const resetCenter = () =>
    setCenter({
      value: totalValue,
      id: 'Total',
    });

  return (
    <div className="flex flex-col h-full w-full bg-white dark:bg-grey-90">
      {/* CLASSES NUMBER AND TIMEFRAMES */}
      <div
        className={`
          flex
          w-full
          items-center
          pl-5
          border-b
          border-grey-10
          overflow-hidden
          dark:border-grey-70
        `}
      ></div>

      <div
        className={`
          flex
          ${width === 1 && 'flex-col'}
          w-full
          h-full
          overflow-auto
        `}
      >
        {/* BAR */}
        {viewType.length === 0 && (
          <Bar
            center={center}
            className={`
              ${width === 1 ? 'flex mx-auto' : 'flex-none'}
              w-88
              ${height === 1 ? 'h-80' : 'h-210'}
              sticky
              top-0
              left-0
              relative
            `}
            barData={dataGraphic}
            margin={parseInt(envVar('BASE_FONT_SIZE')) * 2}
            symbol={symbol}
            strategyValue={totalValue}
            barColors={barColors}
            onClick={() => {}}
            resetCenter={resetCenter}
            setCenter={setCenter}
          />
        )}
        {/* PIE */}
        {viewType.length > 0 && (
          <Pie
            center={center}
            className={`
              ${width === 1 ? 'flex mx-auto' : 'flex-none'}
              w-88
              ${height === 1 ? 'h-80' : 'h-210'}
              sticky
              top-0
              left-0
              relative
            `}
            margin={parseInt(envVar('BASE_FONT_SIZE')) * 2}
            symbol={symbol}
            totalValue={totalValue}
            resetCenter={resetCenter}
            setCenter={setCenter}
            pieData={dataGraphic as PieData[]}
            pieColors={Object.values(CHART_COLORS_HEX)}
            onClick={() => {}}
            handlePieCenter={() => {}}
          />
        )}

        {/* TABLE */}

        <div
          className={`
            overflow-auto
            ${
              width > 1
                ? 'w-full border-l border-grey-10 dark:border-grey-70'
                : 'border-t border-grey-5 dark:border-grey-70'
            }
          `}
        >
          {(width > 1 || height > 1) && (
            <GroupsList
              columns={columns}
              column={width === 1}
              center={center}
              tableData={tableData}
              // interval={interval}
              symbol={symbol}
              // strategyValue={strategyValue}
              widgetWidth={width}
              viewType={viewType}
              resetCenter={resetCenter}
              setViewType={setViewType}
              setCenter={setCenter}
            />
          )}
        </div>
      </div>
    </div>
  );
};
